import React from "react";
import { Avatar, Box, Chip, Paper, Stack, Tooltip, Typography } from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import moment from "moment";

export const MANAGER_ROLES = ["Admin", "Admin-a", "HR Management", "HR"];

export const BRAND_GRADIENT = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
export const GOLD_GRADIENT = "linear-gradient(135deg, #f7d774 0%, #e2a93b 50%, #c8871c 100%)";

const AVATAR_COLORS = ["#667eea", "#764ba2", "#0ea5e9", "#14b8a6", "#f97316", "#ec4899", "#8b5cf6", "#22c55e"];

export const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("") || "?";

export const avatarColor = (seed = "") => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export const formatMonth = (month) => (month ? moment(month, "YYYY-MM").format("MMMM YYYY") : "");

// Award dates are stored as UTC midnight (date-only), so format them in UTC.
export const formatAwardDate = (date, fmt = "DD MMM YYYY") => (date ? moment.utc(date).format(fmt) : "");

// The most recent award counts as "current" while its month is this month or last month.
export const getCurrentAward = (awards = []) => {
  const latest = [...awards].sort((a, b) => b.month.localeCompare(a.month))[0];
  if (!latest) return null;
  return latest.month >= moment().subtract(1, "month").format("YYYY-MM") ? latest : null;
};

// Days until the next birthday (0 = today). `birthday` is { day, month } with month 1-12.
// Feb-29 birthdays fall on Feb-28 in non-leap years.
export const daysUntilBirthday = (birthday) => {
  if (!birthday?.day || !birthday?.month) return null;
  const today = moment().startOf("day");
  const nextFor = (year) => {
    const leap = moment([year]).isLeapYear();
    const day = birthday.month === 2 && birthday.day === 29 && !leap ? 28 : birthday.day;
    return moment({ year, month: birthday.month - 1, day });
  };
  let next = nextFor(today.year());
  if (next.isBefore(today)) next = nextFor(today.year() + 1);
  return next.diff(today, "days");
};

export const birthdayLabel = (birthday) =>
  birthday ? moment({ year: 2000, month: birthday.month - 1, day: birthday.day }).format("DD MMMM") : null;

export const tenureLabel = (joiningDate) => {
  if (!joiningDate || !moment(joiningDate).isValid()) return null;
  const start = moment(joiningDate);
  const years = moment().diff(start, "years");
  const months = moment().diff(start.clone().add(years, "years"), "months");
  if (years <= 0 && months <= 0) return "Joined recently";
  return [years > 0 && `${years} yr${years > 1 ? "s" : ""}`, months > 0 && `${months} mo`].filter(Boolean).join(" ");
};

export const UserAvatar = ({ name, size = 48, ring = false, sx }) => (
  <Avatar
    sx={{
      width: size,
      height: size,
      fontSize: size * 0.38,
      fontWeight: 700,
      bgcolor: avatarColor(name),
      border: ring ? "4px solid #fff" : "none",
      boxShadow: ring ? "0 8px 24px rgba(0,0,0,0.18)" : "none",
      ...sx,
    }}
  >
    {getInitials(name)}
  </Avatar>
);

// Small gold chip shown wherever a recognised employee appears.
export const AwardChip = ({ label = "Employee of the Month", size = "small", sx }) => (
  <Chip
    icon={<EmojiEventsIcon sx={{ color: "#7a4f00 !important" }} />}
    label={label}
    size={size}
    sx={{
      background: GOLD_GRADIENT,
      color: "#4a3000",
      fontWeight: 700,
      boxShadow: "0 2px 8px rgba(226,169,59,0.45)",
      ...sx,
    }}
  />
);

export const InfoTile = ({ icon, label, value, accent = "#667eea" }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      height: "100%",
      borderRadius: 3,
      border: "1px solid #eef0f6",
      display: "flex",
      alignItems: "center",
      gap: 1.75,
      transition: "all .2s",
      "&:hover": { borderColor: `${accent}55`, boxShadow: "0 6px 18px rgba(102,126,234,0.10)" },
    }}
  >
    <Box
      sx={{
        width: 42,
        height: 42,
        flexShrink: 0,
        borderRadius: 2,
        display: "grid",
        placeItems: "center",
        bgcolor: `${accent}14`,
        color: accent,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: "#8a94a6", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Box sx={{ fontWeight: 600, color: "#1f2937", wordBreak: "break-word" }}>{value || <span style={{ color: "#b0b7c3" }}>—</span>}</Box>
    </Box>
  </Paper>
);

/**
 * Gradient cover + avatar + identity. `isCurrentWinner` adds the gold ring and
 * the "Recognition award" ribbon.
 */
export const ProfileHero = ({ name, role, department, email, isTeamLeader, awards = [], isCurrentWinner, isBirthdayToday, actions }) => (
  <Paper elevation={0} sx={{ borderRadius: 4, overflow: "hidden", border: "1px solid #eef0f6", mb: 3 }}>
    <Box
      sx={{
        height: { xs: 110, md: 150 },
        background: isCurrentWinner ? `${GOLD_GRADIENT}, ${BRAND_GRADIENT}` : BRAND_GRADIENT,
        backgroundBlendMode: "overlay",
        position: "relative",
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 15% 30%, rgba(255,255,255,.25) 0, transparent 35%), radial-gradient(circle at 85% 80%, rgba(255,255,255,.18) 0, transparent 30%)",
        },
      }}
    >
      {isCurrentWinner && (
        <Box
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.75,
            py: 0.75,
            borderRadius: 999,
            bgcolor: "rgba(255,255,255,0.92)",
            color: "#7a4f00",
            fontWeight: 800,
            fontSize: 13,
            boxShadow: "0 4px 14px rgba(0,0,0,.15)",
          }}
        >
          <EmojiEventsIcon sx={{ fontSize: 18, color: "#e2a93b" }} /> Current Employee of the Month
        </Box>
      )}
      {isBirthdayToday && (
        <Box
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
            zIndex: 1,
            px: 1.75,
            py: 0.75,
            borderRadius: 999,
            bgcolor: "rgba(255,255,255,0.92)",
            color: "#be185d",
            fontWeight: 800,
            fontSize: 13,
            boxShadow: "0 4px 14px rgba(0,0,0,.15)",
          }}
        >
          🎂 Birthday today!
        </Box>
      )}
    </Box>
    <Box sx={{ px: { xs: 2, md: 4 }, pb: 3, mt: { xs: -5, md: -6 }, position: "relative" }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2.5} alignItems={{ xs: "flex-start", md: "flex-end" }}>
        <Box sx={{ position: "relative" }}>
          <UserAvatar
            name={name}
            size={112}
            ring
            sx={isCurrentWinner ? { border: "4px solid #f7d774", boxShadow: "0 0 0 4px #fff, 0 10px 30px rgba(226,169,59,.55)" } : undefined}
          />
          {awards.length > 0 && (
            <Tooltip title={`${awards.length} Recognition award${awards.length > 1 ? "s" : ""}`}>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 4,
                  right: 4,
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  background: GOLD_GRADIENT,
                  display: "grid",
                  placeItems: "center",
                  border: "3px solid #fff",
                }}
              >
                <WorkspacePremiumIcon sx={{ fontSize: 18, color: "#4a3000" }} />
              </Box>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, pt: { md: 7 } }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: "#1f2937", lineHeight: 1.2 }}>
            {name}
          </Typography>
          <Typography sx={{ color: "#6b7280", mt: 0.5 }}>{email}</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", rowGap: 1 }}>
            {role && <Chip label={role} size="small" sx={{ bgcolor: "#eef0ff", color: "#4c51bf", fontWeight: 600 }} />}
            {department && <Chip label={department} size="small" sx={{ bgcolor: "#f3e8ff", color: "#6b21a8", fontWeight: 600 }} />}
            {isTeamLeader && <Chip label="Team Leader" size="small" sx={{ bgcolor: "#e0f2fe", color: "#0369a1", fontWeight: 600 }} />}
            {awards.length > 0 && <AwardChip label={`${awards.length}× Employee of the Month`} />}
          </Stack>
        </Box>
        {actions && <Box sx={{ alignSelf: { xs: "stretch", md: "flex-end" } }}>{actions}</Box>}
      </Stack>
    </Box>
  </Paper>
);

// Recognition award cards. Rendered only when the user has at least one award.
export const RecognitionSection = ({ awards = [], latestMonth }) => {
  if (!awards.length) return null;
  return (
    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, border: "1px solid #f3e3b5", mb: 3, bgcolor: "#fffdf6" }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
        <EmojiEventsIcon sx={{ color: "#e2a93b" }} />
        <Typography variant="h6" sx={{ fontWeight: 800, color: "#4a3000" }}>
          Recognition Awards
        </Typography>
      </Stack>
      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(auto-fill, minmax(260px, 1fr))" } }}>
        {awards.map((a) => {
          const isLatest = a.month === latestMonth;
          return (
            <Box
              key={a._id}
              sx={{
                position: "relative",
                p: 2.5,
                borderRadius: 3,
                overflow: "hidden",
                background: isLatest ? GOLD_GRADIENT : "linear-gradient(135deg, #fff8e6 0%, #fdf0cf 100%)",
                color: "#4a3000",
                border: "1px solid #f0d58c",
                boxShadow: isLatest ? "0 10px 26px rgba(226,169,59,.35)" : "none",
              }}
            >
              <WorkspacePremiumIcon sx={{ position: "absolute", right: -10, bottom: -14, fontSize: 110, opacity: 0.15 }} />
              <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 1.2 }}>
                {a.title || "Employee of the Month"}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                {formatMonth(a.month)}
              </Typography>
              {a.citation && (
                <Typography variant="body2" sx={{ mt: 1.25, fontStyle: "italic", opacity: 0.9 }}>
                  “{a.citation}”
                </Typography>
              )}
              <Typography variant="caption" sx={{ display: "block", mt: 1.5, fontWeight: 600, opacity: 0.8 }}>
                Announced {formatAwardDate(a.announcedAt)}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};
